import type { FC, ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import type { ShipmentDto } from '@/api';
import { cn } from '@/ui';

import {
  ShipmentBadges,
  ShipmentOriginBadge,
} from '../../components/ShipmentBadges';
import { ShipmentLinesCell } from '../../components/ShipmentLinesCell';
import { today } from '../../lib/dates';
import { formatDate, formatMoney, toNumber } from '../../lib/format';
import { resolveNextTaskText } from '../../lib/nextTask';
import { overdueDays } from '../../lib/paymentSchedule';
import { FINISHED_SHIPMENT_STATUSES } from '../../lib/shipmentStatus';

/*
 * Карточка списка «Отгрузки в работе» - .ship-card макета (v4): номер и бейджи,
 * клиент с городом и товары, даты и сумма, «Текущая задача». От 1280px - четыре
 * колонки, уже - одна, как в макете.
 *
 * Карточка целиком - ссылка: имя клиента растянуто на неё псевдоэлементом. Что
 * должно отвечать на свой клик или подсказку (раскрытие товаров, бейджи с
 * причиной задержки и номером заказа), поднято над ним.
 */
export const ShipmentCard: FC<{ shipment: ShipmentDto }> = ({ shipment }) => {
  const task = resolveNextTaskText(shipment);
  const isClosed = FINISHED_SHIPMENT_STATUSES.includes(shipment.status);
  const isPaymentOverdue =
    !isClosed && overdueDays(shipment.paymentDueDate, today()) !== null;

  return (
    <article className="relative grid gap-3.5 rounded-lg border border-ink-200 bg-white px-[22px] py-5 transition-[border-color,box-shadow] hover:border-ink-300 hover:shadow-md has-[a:focus-visible]:ring-2 has-[a:focus-visible]:ring-ring xl:grid-cols-[170px_minmax(0,1.5fr)_170px_minmax(0,1.2fr)] xl:items-center xl:gap-x-5 xl:gap-y-2">
      <div className="flex items-start justify-between gap-[7px] xl:row-span-2 xl:flex-col xl:justify-start">
        <span className="text-[12.5px] font-semibold whitespace-nowrap text-ink-500 tabular-nums">
          № {shipment.number}
        </span>
        <span className="relative z-10 inline-flex flex-wrap items-center gap-1.5 max-xl:justify-end">
          <ShipmentBadges
            className="contents"
            status={shipment.status}
            handoverState={shipment.handoverState}
            onecDiscrepancies={shipment.onecDiscrepancies}
            isDelayed={shipment.isDelayed}
            delayReason={shipment.delayReason}
          />
          <ShipmentOriginBadge
            origin={shipment.origin}
            customerOrderNumber={shipment.customerOrderNumber}
            isForecast={shipment.isForecast}
          />
        </span>
      </div>

      <div className="min-w-0 xl:col-start-2 xl:row-start-1">
        <Link
          to="/shipments/$shipmentId"
          params={{ shipmentId: shipment.id }}
          className="text-base font-semibold text-ink-900 outline-none after:absolute after:inset-0 after:rounded-lg after:content-['']"
        >
          {shipment.partnerName}
        </Link>
        {shipment.deliveryCity && (
          <div className="mt-px text-[12.5px] text-ink-500">
            {shipment.deliveryCity}
          </div>
        )}
      </div>

      <div className="min-w-0 rounded-[8px] border border-ink-150 bg-ink-50 px-3.5 py-3 xl:col-start-2 xl:row-start-2">
        <span className="block text-xs text-ink-500">Товар</span>
        {/* Пилюля белая на серой подложке, как .pos-more внутри .ship-card-product. */}
        <div className="relative z-10 mt-1 w-fit max-w-full [&_button]:bg-white">
          <ShipmentLinesCell lines={shipment.lines} />
        </div>
      </div>

      <div className="flex flex-col gap-2 xl:col-start-3 xl:row-span-2 xl:row-start-1">
        <Row label="План. дата">{formatDate(shipment.plannedDate)}</Row>
        <Row label="Оплата до">
          <span className={cn(isPaymentOverdue && 'text-st-delayed')}>
            {shipment.paymentDueDate
              ? formatDate(shipment.paymentDueDate)
              : '-'}
          </span>
        </Row>
        <Row label="Сумма">
          {formatMoney(toNumber(shipment.amount))}
          {shipment.hasLinesWithoutPrice && (
            <span className="block text-[11.5px] font-normal text-st-approval">
              есть без цены
            </span>
          )}
        </Row>
      </div>

      <div className="border-ink-100 max-xl:border-t max-xl:pt-3 xl:col-start-4 xl:row-span-2 xl:row-start-1 xl:border-l xl:pl-5">
        <span className="text-xs text-ink-500">Текущая задача</span>
        <div className="mt-[3px] text-[13.5px] leading-[1.4] text-ink-700">
          {task?.title ?? 'Нет: отгрузка закрыта'}
        </div>
      </div>
    </article>
  );
};

/** Строка «подпись - значение» - .scr макета. */
const Row: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-baseline justify-between gap-3">
    <span className="shrink-0 text-xs text-ink-500">{label}</span>
    <span className="text-right text-[13.5px] font-medium text-ink-800">
      {children}
    </span>
  </div>
);
